/**
 * <cart-rewards> — auto-add / auto-remove gift products tied to cart reward tiers.
 *
 * Each marker inside the element is one gift SLOT:
 *   data-tier-achieved          "true" | "false"      — whether the tier is met (server-rendered)
 *   data-tier-gift-variant-id   variant id (numeric)  — which variant the slot should hold
 *   data-tier-gift-quantity     integer               — how many units of that variant the slot holds now
 *   data-tier-gift-for          paid variant id       — optional: the slot belongs to one paid product
 *                                                       (1+1 cream: a free cream per paid cream unit)
 *   data-tier-gift-units        integer               — optional: how many units the slot should hold
 *   data-tier-gift-unit         integer               — optional: which unit of the paid variant this
 *                                                       slot serves (1..n). Every paid unit gets its own
 *                                                       slot and its own gift line, so each free cream
 *                                                       can be a different variant.
 *
 * A slot without data-tier-gift-for is a plain threshold gift (the oil over 20 €): one unit,
 * cart lines matched by variant. A slot with it holds data-tier-gift-units free units, and its
 * cart lines are the ones whose `_gift_for` property names that paid variant. Two paid lines of
 * the same variant render two markers with the same key; their units add up.
 *
 * <cart-rewards> also carries data-gift-units-total — every auto-gift unit in the cart — so a
 * gift that no marker accounts for any more (its paid cream just left the cart) is noticed.
 *
 * Sync rules, per slot:
 *   achieved && nothing in cart   →  add the variant (line properties _auto_gift, _gift_for)
 *   achieved && wrong quantity    →  set it to the slot's units
 *   lines of another variant      →  remove (the customer picked a different gift)
 *   !achieved                     →  remove the slot's lines
 *   gift lines no achieved slot claims  →  remove
 *
 * That is what lets <gift-picker> (bottom of this file) work: the picker only writes the
 * customer's choice into a cart attribute and repaints. The drawer comes back with the slot
 * pointing at the newly chosen variant, so the old line is the wrong variant and goes.
 *
 * The server-rendered markers only decide WHETHER there is work to do. Every write is then
 * planned against a fresh /cart.js read, because a drawer repaint can arrive carrying markup
 * that was rendered before an earlier gift add had landed — acting on that stale markup is
 * what used to put the same gift in the cart twice.
 *
 * The last cart mutation asks Shopify for the drawer sections in the same request
 * (`sections` / `sections_url`), so the refreshed markup arrives with the write
 * instead of costing two extra round-trips afterwards.
 */
class CartRewards extends HTMLElement {
  static busy = false;
  static rerun = false;
  static SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  connectedCallback() {
    this.sync();
  }

  async sync() {
    // A repaint that lands mid-write carries stale markup. Note it down and re-check once the
    // write is finished, instead of either acting on it now or dropping it silently.
    if (CartRewards.busy) {
      CartRewards.rerun = true;
      return;
    }

    const slots = this.slots();
    if (slots.size === 0) return;
    if (!this.needsWork(slots)) return;

    CartRewards.busy = true;
    try {
      const { toChange, toAdd } = await this.plan(slots);
      if (toChange.length === 0 && toAdd.length === 0) return;

      // Only the final mutation carries the section request — earlier ones would
      // return markup that the next write immediately invalidates.
      let state = null;
      if (toChange.length > 0) state = await this.changeLines(toChange, toAdd.length === 0);
      if (toAdd.length > 0) state = await this.addGifts(toAdd);

      if (state && state.sections) {
        this.renderSections(state.sections);
      } else {
        // A rejected write (or an empty change list) leaves us without fresh markup.
        await this.refreshCart();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-rewards' });
      }
    } catch (e) {
      console.error('[cart-rewards] sync failed', e);
    } finally {
      CartRewards.busy = false;
      if (CartRewards.rerun) {
        CartRewards.rerun = false;
        const live = document.querySelector('cart-rewards');
        if (live) live.sync();
      }
    }
  }

  /** Variants Shopify rejected earlier in this page session — don't keep retrying them. */
  failedVariants() {
    try {
      return JSON.parse(sessionStorage.getItem('cart-rewards:failed') || '[]');
    } catch (_) {
      return []; // sessionStorage unavailable — fine
    }
  }

  markFailed(variantId) {
    try {
      const failed = this.failedVariants();
      if (!failed.includes(variantId)) {
        failed.push(variantId);
        sessionStorage.setItem('cart-rewards:failed', JSON.stringify(failed));
      }
    } catch (_) { /* sessionStorage unavailable — fine */ }
  }

  /**
   * Read the markers into slots, keyed the same way cartGifts() keys the cart lines.
   * @returns {Map<string, {variantId: string, giftFor: string, achieved: boolean, units: number, quantity: number}>}
   */
  slots() {
    const slots = new Map();

    for (const marker of this.querySelectorAll('[data-tier-gift-variant-id]')) {
      const variantId = marker.dataset.tierGiftVariantId;
      if (!variantId) continue;

      const giftFor = marker.dataset.tierGiftFor || '';
      const unit = giftFor ? marker.dataset.tierGiftUnit || '' : '';
      const key = CartRewards.slotKey(variantId, giftFor, unit);
      // data-tier-until (unix seconds): past it the tier no longer counts, whatever the markup says.
      const until = parseInt(marker.dataset.tierUntil, 10) || 0;
      const achieved = marker.dataset.tierAchieved === 'true' && !(until && Date.now() >= until * 1000);
      // Older markup has no quantity attribute; fall back to the in-cart flag.
      const quantity =
        marker.dataset.tierGiftQuantity === undefined
          ? (marker.dataset.tierGiftInCart === 'true' ? 1 : 0)
          : parseInt(marker.dataset.tierGiftQuantity, 10) || 0;
      const units = giftFor ? parseInt(marker.dataset.tierGiftUnits, 10) || 1 : 1;

      const slot = slots.get(key);
      if (!slot) {
        slots.set(key, { variantId, giftFor, unit, achieved, units, quantity });
      } else if (giftFor) {
        // Same paid variant on two lines (different properties): one slot, units add up.
        slot.units += units;
        slot.achieved = slot.achieved || achieved;
      } else {
        slot.achieved = slot.achieved || achieved;
      }
    }

    return slots;
  }

  static slotKey(variantId, giftFor, unit = '') {
    if (!giftFor) return `v:${variantId}`;
    return unit ? `for:${giftFor}#${unit}` : `for:${giftFor}`;
  }

  /** Whether the server-rendered markup shows anything to fix. Cheap — no network. */
  needsWork(slots) {
    const failed = this.failedVariants();
    let claimed = 0;

    for (const slot of slots.values()) {
      if (!slot.achieved) {
        if (slot.quantity > 0) return true;
        continue;
      }
      claimed += slot.quantity;
      if (slot.quantity === 0 && !failed.includes(slot.variantId)) return true;
      if (slot.quantity > slot.units) return true;
      if (slot.quantity > 0 && slot.quantity < slot.units && !failed.includes(slot.variantId)) return true;
    }

    const total = parseInt(this.dataset.giftUnitsTotal, 10);
    return !Number.isNaN(total) && total !== claimed;
  }

  /**
   * Turn the slots into concrete writes, checked against the live cart.
   * @returns {Promise<{toChange: {key: string, quantity: number, variantId: string}[], toAdd: {variantId: string, quantity: number, giftFor: string}[]}>}
   */
  async plan(slots) {
    const gifts = await this.cartGifts(slots);
    const failed = this.failedVariants();
    const toChange = [];
    const toAdd = [];

    // A line can be reached by both loops below; schedule each one once.
    const scheduled = new Set();
    const change = (line, quantity) => {
      if (scheduled.has(line.key)) return;
      scheduled.add(line.key);
      toChange.push({ key: line.key, quantity, variantId: line.variantId });
    };

    for (const [key, slot] of slots) {
      const lines = gifts.get(key) || [];

      if (!slot.achieved) {
        for (const line of lines) change(line, 0);
        continue;
      }

      const right = lines.filter((line) => line.variantId === slot.variantId);
      for (const line of lines) {
        if (line.variantId !== slot.variantId) change(line, 0);
      }

      if (right.length === 0) {
        if (!failed.includes(slot.variantId)) {
          toAdd.push({ variantId: slot.variantId, quantity: slot.units, giftFor: slot.giftFor, unit: slot.unit });
        }
        continue;
      }

      // Keep the first line at the slot's units and drop whatever else accumulated. When
      // Shopify already refused to grow this variant (stock), only ever shrink it.
      const [first, ...rest] = right;
      const target = failed.includes(slot.variantId) ? Math.min(first.quantity, slot.units) : slot.units;
      if (first.quantity !== target) change(first, target);
      for (const line of rest) change(line, 0);
    }

    // A free line no achieved slot claims — its paid cream left the cart, or the tier that
    // put it there is gone. Left alone it would ride to checkout.
    for (const [key, lines] of gifts) {
      const slot = slots.get(key);
      if (slot && slot.achieved) continue;
      for (const line of lines) change(line, 0);
    }

    return { toChange, toAdd };
  }

  /**
   * Gift lines currently in the cart, keyed like the slots.
   *
   * A gift variant can also reach the cart without _auto_gift — the customer opens the gift's
   * own product page from the drawer and adds it by hand. Such a free line of a variant some
   * slot hands out still counts as that gift, so it is trimmed or removed like the auto-added
   * one instead of riding to checkout as a second free unit.
   * @returns {Promise<Map<string, {key: string, quantity: number, variantId: string}[]>>}
   */
  async cartGifts(slots) {
    const gifts = new Map();
    const giftVariants = new Set([...slots.values()].map((slot) => slot.variantId));

    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) return gifts;
    const cart = await res.json();

    // Products the gifts come from: what a slot hands out, or what an auto-gift line already is.
    // A free line of such a product without _auto_gift is a stray gift even when it is another
    // variant than the one its slot hands out now (#171200: the slot gave the day cream, the
    // stray line was the night cream). Keyed by variant, no cream slot claims it, so it goes.
    // Free lines of other products (an app's own gift) are left alone.
    const giftProducts = new Set();
    for (const item of cart.items) {
      const auto = Boolean(item.properties && item.properties._auto_gift);
      if (auto || giftVariants.has(String(item.variant_id))) giftProducts.add(item.product_id);
    }

    for (const item of cart.items) {
      const variantId = String(item.variant_id);
      const auto = Boolean(item.properties && item.properties._auto_gift);
      const stray =
        !auto && item.final_line_price === 0 && (giftVariants.has(variantId) || giftProducts.has(item.product_id));
      if (!auto && !stray) continue;
      const key = auto
        ? CartRewards.slotKey(variantId, item.properties._gift_for || '', item.properties._gift_unit || '')
        : CartRewards.slotKey(variantId, '');
      const lines = gifts.get(key) || [];
      lines.push({ key: item.key, quantity: item.quantity, variantId });
      gifts.set(key, lines);
    }

    return gifts;
  }

  sectionParams() {
    return {
      sections: CartRewards.SECTIONS.join(','),
      sections_url: window.location.pathname,
    };
  }

  async changeLines(changes, withSections) {
    let state = null;

    for (const [index, change] of changes.entries()) {
      const isLast = index === changes.length - 1;
      const body = { id: change.key, quantity: change.quantity };
      if (withSections && isLast) Object.assign(body, this.sectionParams());

      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        state = await res.json();
      } else if (change.quantity > 0) {
        // Most likely not enough stock for that many free units — stop asking for more.
        console.error(`[cart-rewards] gift line could not be set to ${change.quantity} (HTTP ${res.status})`);
        this.markFailed(change.variantId);
      }
    }

    return state;
  }

  async addGifts(gifts) {
    // Add gifts one-by-one so a single failing item doesn't reject the whole batch
    let state = null;

    for (const [index, gift] of gifts.entries()) {
      const isLast = index === gifts.length - 1;
      const properties = { _auto_gift: String(gift.variantId) };
      if (gift.giftFor) properties._gift_for = String(gift.giftFor);
      if (gift.unit) properties._gift_unit = String(gift.unit);
      const body = { id: parseInt(gift.variantId, 10), quantity: gift.quantity, properties };
      if (isLast) Object.assign(body, this.sectionParams());

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });

      if (res.ok) {
        state = await res.json();
        continue;
      }

      let detail;
      try { detail = await res.json(); } catch { detail = await res.text(); }
      console.error(
        `[cart-rewards] gift variant ${gift.variantId} could not be added (HTTP ${res.status})`,
        detail
      );
      // Mark this variant as failed for the rest of the page session so we don't retry on every render
      this.markFailed(gift.variantId);
    }

    return state;
  }

  /** @param {Record<string, string>} sections - section id → rendered HTML */
  renderSections(sections) {
    CartRewards.renderSections(sections);
  }

  /**
   * Paint the drawer + cart icon from section markup returned by a cart mutation.
   * Static, because <gift-picker> repaints too and the element it would call is the very
   * one being replaced.
   * @param {Record<string, string>} sections - section id → rendered HTML
   */
  static renderSections(sections) {
    const drawerHtml = sections['cart-drawer'];
    if (drawerHtml) {
      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      for (const selector of ['cart-rewards', 'cart-drawer-items', '.cart-drawer__footer']) {
        const target = document.querySelector(selector);
        const source = drawerDoc.querySelector(selector);
        if (target && source) target.replaceWith(source);
      }
    }

    const bubbleHtml = sections['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const bubbleSrc =
        bubbleDoc.getElementById('shopify-section-cart-icon-bubble') || bubbleDoc.querySelector('.shopify-section');
      const bubbleTgt = document.getElementById('shopify-section-cart-icon-bubble');
      if (bubbleSrc && bubbleTgt) bubbleTgt.innerHTML = bubbleSrc.innerHTML;
    }
  }

  /** Fallback when a mutation didn't return sections (e.g. every add was rejected). */
  async refreshCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    const responses = await Promise.all(
      CartRewards.SECTIONS.map((id) => fetch(`${cartUrl}?section_id=${id}`))
    );
    const htmls = await Promise.all(responses.map((r) => r.text()));

    this.renderSections(
      Object.fromEntries(CartRewards.SECTIONS.map((id, i) => [id, htmls[i]]))
    );
  }
}

if (!customElements.get('cart-rewards')) {
  customElements.define('cart-rewards', CartRewards);
}

/**
 * <gift-picker> — lets the customer choose WHICH variant of a multi-variant auto-gift they get
 * (e.g. which face cream is the free one), separately for every paid cream UNIT: three paid
 * day creams are three gift lines, each with its own picker.
 *
 * The picker never touches the cart lines itself. It writes the choice into the cart attribute
 * `_gift_map` — ",<paid variant>.<unit>:<gift variant>,..." — and asks for fresh drawer markup in the
 * same request; the drawer then renders each slot against that map and <cart-rewards> does the
 * swap. One writer for gift lines means the picker can't race the tier sync.
 */
class GiftPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) this.choose(event.target.value);
    });
  }

  async choose(variantId) {
    const giftFor = this.dataset.giftFor;
    if (!variantId || !giftFor) return;
    // Older markup has no unit: the choice then covers every unit of that paid cream.
    const entryKey = this.dataset.giftUnit ? `${giftFor}.${this.dataset.giftUnit}` : giftFor;
    this.setAttribute('aria-busy', 'true');

    try {
      const current = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const entries = String((current.attributes && current.attributes._gift_map) || '')
        .split(',')
        .filter((entry) => entry && !entry.startsWith(`${entryKey}:`));
      entries.push(`${entryKey}:${variantId}`);

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { _gift_map: `,${entries.join(',')},` },
          sections: CartRewards.SECTIONS.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const state = await res.json();
      if (state.sections) {
        // Replaces <cart-rewards>, whose connectedCallback then swaps the gift line.
        CartRewards.renderSections(state.sections);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-picker' });
      }
    } catch (e) {
      console.error('[gift-picker] choice failed', e);
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('gift-picker')) {
  customElements.define('gift-picker', GiftPicker);
}
